/**
 * SetupWizard (Phase 2, §7.5) — the guided 3-step Enable-Banking onboarding,
 * behind a provider chooser header (Phase 4, §7.3).
 *
 * The header shows two option cards, "Enable Banking" and
 * "FinTS — Hamburger Sparkasse"; the FinTS card renders only when
 * `useFintsAvailability().data?.enabled` (the channel is config-gated —
 * §1/§10) and navigates to the dedicated `FintsSetupPage`. The EB flow below
 * is untouched:
 *
 *  1. "EB-App" — create the app in the EB Control Panel, register the redirect
 *     and upload the certificate, then paste `application_id` + the RSA private
 *     key (PEM) and pick the environment. The key is submitted once via
 *     `useSaveCredential` (POST body); it is never rendered back or put in a URL.
 *     This step is skipped when a credential already exists.
 *  2. "Aktivieren" — restricted production apps start Inactive; activate each
 *     bank via "Aktivieren durch Kontoverknüpfung" in the Control Panel.
 *  3. "Bank verbinden" — pick a bank (`AspspPicker`) → `useAuthorizeConnection`
 *     → redirect the browser to the bank's SCA page.
 */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { Field } from '../../components/ui/Field.tsx';
import { Input } from '../../components/ui/Input.tsx';
import { Textarea } from '../../components/ui/Textarea.tsx';
import { SegmentedControl } from '../../components/ui/SegmentedControl.tsx';
import { SkeletonList } from '../../components/ui/Skeleton.tsx';
import { useToast } from '../../context/ToastContext.tsx';
import { EB_ENVIRONMENT } from '../../lib/labels.ts';
import { useProviderCredential } from '../../lib/hooks/useProviderCredential.ts';
import { useAspsps } from '../../lib/hooks/useAspsps.ts';
import {
  useAuthorizeConnection,
  useSaveCredential,
} from '../../lib/hooks/useConnectionMutations.ts';
import { useFintsAvailability } from '../../lib/hooks/useFints.ts';
import type { AspspOut, EbEnvironment } from '../../lib/types.ts';
import { AspspPicker } from './components/AspspPicker.tsx';

const REDIRECT_URL = 'https://budget.matzradloff.info/api/connections/eb/callback';

type Step = 1 | 2 | 3;

const STEPS: { step: Step; label: string }[] = [
  { step: 1, label: 'EB-App' },
  { step: 2, label: 'Aktivieren' },
  { step: 3, label: 'Bank verbinden' },
];

export default function SetupWizard() {
  const navigate = useNavigate();
  const credentialQuery = useProviderCredential();
  // The step the user has explicitly navigated to; null until they move. The
  // effective step is derived so step 1 is skipped when a credential already
  // exists (§7.5) — no setState-in-effect needed.
  const [navStep, setNavStep] = useState<Step | null>(null);

  const hasCredential = credentialQuery.data != null;
  const step: Step = navStep ?? (hasCredential ? 2 : 1);
  const setStep = setNavStep;

  return (
    <div>
      <PageHeader
        title="Bank verbinden"
        actions={
          <Button variant="secondary" size="sm" onClick={() => void navigate('/bankverbindungen')}>
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Abbrechen</span>
          </Button>
        }
      />

      <div className="w-full space-y-8 px-4 py-6 sm:px-6">
        <ProviderChooser />
        <Stepper current={step} />

        {credentialQuery.isPending ? (
          <SkeletonList rows={3} />
        ) : step === 1 ? (
          <StepCredential onDone={() => setStep(2)} />
        ) : step === 2 ? (
          <StepActivate
            onBack={hasCredential ? undefined : () => setStep(1)}
            onNext={() => setStep(3)}
          />
        ) : (
          <StepConnect enabled={hasCredential} onBack={() => setStep(2)} />
        )}
      </div>
    </div>
  );
}

function Stepper({ current }: { current: Step }) {
  return (
    <ol className="flex items-center gap-2">
      {STEPS.map(({ step, label }, i) => {
        const active = step === current;
        const done = step < current;
        return (
          <li key={step} className="flex flex-1 items-center gap-2">
            <span
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                active
                  ? 'bg-sky-600 text-white dark:bg-sky-500'
                  : done
                    ? 'bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300'
                    : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
              }`}
            >
              {step}
            </span>
            <span
              className={`truncate text-xs font-medium ${
                active ? 'text-slate-900 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              {label}
            </span>
            {i < STEPS.length - 1 ? (
              <span className="h-px flex-1 bg-slate-100 dark:bg-slate-800/70" />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

// Flat surface (DESIGN.md §1): steps sit directly on the page — no card chrome.
function Card({ children }: { children: ReactNode }) {
  return <div className="space-y-5">{children}</div>;
}

/**
 * Provider chooser header (Phase 4, §7.3): "Enable Banking" is the active flow
 * below this header; the FinTS card is a plain link out to `FintsSetupPage`
 * and renders only when the channel is configured server-side.
 */
function ProviderChooser() {
  const navigate = useNavigate();
  const fintsAvailability = useFintsAvailability();
  const fintsEnabled = fintsAvailability.data?.enabled ?? false;

  return (
    <div className={`grid gap-3 ${fintsEnabled ? 'sm:grid-cols-2' : ''}`}>
      <div className="rounded-md bg-sky-50 px-4 py-3 dark:bg-sky-950/40">
        <p className="text-sm font-semibold text-sky-800 dark:text-sky-300">Enable Banking</p>
        <p className="mt-0.5 text-xs text-sky-700 dark:text-sky-400">
          DKB, Commerzbank und weitere Banken per SCA-Weiterleitung.
        </p>
      </div>
      {fintsEnabled ? (
        <button
          type="button"
          onClick={() => void navigate('/bankverbindungen/einrichten/fints')}
          className="rounded-md bg-slate-50 px-4 py-3 text-left transition-colors duration-150 hover:bg-slate-100 dark:bg-slate-800/60 dark:hover:bg-slate-800"
        >
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            FinTS — Hamburger Sparkasse
          </p>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Direkter PIN/TAN-Zugang per S-pushTAN.
          </p>
        </button>
      ) : null}
    </div>
  );
}

function StepCredential({ onDone }: { onDone: () => void }) {
  const { toast } = useToast();
  const save = useSaveCredential();
  const [applicationId, setApplicationId] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [environment, setEnvironment] = useState<EbEnvironment>('production');
  const [error, setError] = useState<string | null>(null);

  function submit() {
    if (!applicationId.trim()) {
      setError('Bitte die Application-ID eingeben.');
      return;
    }
    if (!privateKey.trim()) {
      setError('Bitte den privaten Schlüssel (PEM) einfügen.');
      return;
    }
    setError(null);
    save.mutate(
      { application_id: applicationId.trim(), private_key: privateKey, environment },
      {
        onSuccess: () => {
          // Clear the key from component state immediately after submit.
          setPrivateKey('');
          toast('Zugangsdaten gespeichert.', { variant: 'success' });
          onDone();
        },
        onError: (err) => setError(err.message),
      },
    );
  }

  return (
    <Card>
      <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
        <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
          1. Enable-Banking-App anlegen
        </p>
        <ol className="list-decimal space-y-1 pl-5">
          <li>
            Erstelle im{' '}
            <a
              href="https://enablebanking.com/cp/"
              target="_blank"
              rel="noreferrer noopener"
              className="text-sky-600 underline transition-colors duration-150 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300"
            >
              Enable-Banking-Control-Panel
            </a>{' '}
            eine Anwendung.
          </li>
          <li>
            Trage als Redirect-URL exakt Folgendes ein:
            <code className="mt-1 block overflow-x-auto rounded-md bg-slate-50 px-2 py-1 text-xs text-slate-800 dark:bg-slate-900 dark:text-slate-200">
              {REDIRECT_URL}
            </code>
          </li>
          <li>Lade das Zertifikat hoch und lade den privaten Schlüssel (.pem) herunter.</li>
        </ol>
      </div>

      <Field label="Application-ID" required>
        {(id) => (
          <Input
            id={id}
            value={applicationId}
            onChange={(e) => setApplicationId(e.target.value)}
            autoComplete="off"
            placeholder="z. B. 12345678-90ab-cdef-1234-567890abcdef"
          />
        )}
      </Field>

      <Field
        label="Privater Schlüssel (PEM)"
        hint="Beginnt mit -----BEGIN PRIVATE KEY-----. Wird verschlüsselt gespeichert und nie erneut angezeigt."
        required
      >
        {(id) => (
          <Textarea
            id={id}
            value={privateKey}
            onChange={(e) => setPrivateKey(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            rows={6}
            className="font-mono text-xs"
            placeholder="-----BEGIN PRIVATE KEY-----&#10;…&#10;-----END PRIVATE KEY-----"
          />
        )}
      </Field>

      <Field label="Umgebung">
        {() => (
          <SegmentedControl
            value={environment}
            onChange={setEnvironment}
            ariaLabel="Umgebung"
            options={[
              { value: 'production', label: EB_ENVIRONMENT.production },
              { value: 'sandbox', label: EB_ENVIRONMENT.sandbox },
            ]}
          />
        )}
      </Field>

      {error ? <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p> : null}

      <div className="flex justify-end">
        <Button onClick={submit} disabled={save.isPending}>
          {save.isPending ? 'Speichern …' : 'Speichern & weiter'}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
}

function StepActivate({ onBack, onNext }: { onBack?: () => void; onNext: () => void }) {
  return (
    <Card>
      <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
        <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
          2. App aktivieren
        </p>
        <p>
          Neue Produktions-Apps starten im Status <strong>Inaktiv</strong> und können zunächst nur
          verknüpfte Konten abrufen.
        </p>
        <p>
          Aktiviere im Control Panel pro Bank die Option{' '}
          <em>„Aktivieren durch Kontoverknüpfung"</em>. Erst danach liefert die Bank Konten und
          Umsätze aus.
        </p>
      </div>

      <div className="flex justify-between">
        {onBack ? (
          <Button variant="secondary" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            Zurück
          </Button>
        ) : (
          <span />
        )}
        <Button onClick={onNext}>
          Weiter
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
}

function StepConnect({ enabled, onBack }: { enabled: boolean; onBack: () => void }) {
  const { toast } = useToast();
  const aspspsQuery = useAspsps('DE', { enabled });
  const authorize = useAuthorizeConnection();
  const [selected, setSelected] = useState<AspspOut | null>(null);

  function connect() {
    if (!selected) return;
    authorize.mutate(
      { aspsp_name: selected.name, aspsp_country: selected.country, psu_type: 'personal' },
      {
        onSuccess: (data) => {
          window.location.href = data.authorization_url;
        },
        onError: (err) => toast(err.message, { variant: 'error' }),
      },
    );
  }

  return (
    <Card>
      <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
        3. Bank auswählen
      </p>

      {aspspsQuery.isPending ? (
        <SkeletonList rows={4} />
      ) : aspspsQuery.isError ? (
        <p className="text-sm text-rose-600 dark:text-rose-400">
          Bankliste konnte nicht geladen werden: {aspspsQuery.error.message}
        </p>
      ) : (
        <AspspPicker
          aspsps={aspspsQuery.data}
          value={selected?.name ?? null}
          onSelect={setSelected}
          disabled={authorize.isPending}
        />
      )}

      <div className="flex justify-between">
        <Button variant="secondary" onClick={onBack} disabled={authorize.isPending}>
          <ArrowLeft className="h-4 w-4" />
          Zurück
        </Button>
        <Button onClick={connect} disabled={!selected || authorize.isPending}>
          {authorize.isPending ? 'Weiterleitung …' : 'Verbinden'}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
}
